import { useMemo } from "react";
import { Topbar } from "../ui/Topbar";
import { useStatsHistory } from "../features/stats/useStatsHistory";
import { EquityCurve } from "../features/stats/EquityCurve";
import { MonthlyBars } from "../features/stats/MonthlyBars";
import {
  recentMonthBuckets,
  summarize,
  topIssuers,
} from "../features/stats/statsData";
import { fmtDate, fmtRub } from "../lib/format";
import { useScrollRestore } from "../lib/useScrollRestore";

const signed = (v: number): string => (v > 0 ? "+" : "") + fmtRub(v);

export default function StatsPage() {
  const now = useMemo(() => new Date(), []);
  const history = useStatsHistory();
  const scrollRef = useScrollRestore<HTMLDivElement>("stats");

  const events = history.data ?? [];
  const sum = useMemo(() => summarize(events, now), [events, now]);
  const months = useMemo(
    () => recentMonthBuckets(events, now, 12),
    [events, now],
  );
  const issuers = useMemo(() => topIssuers(events, 3), [events]);

  // ширины сегментов структуры источников — по модулю вклада
  const structTotal =
    Math.abs(sum.coupons) + Math.abs(sum.sells) + Math.abs(sum.redemptions) || 1;
  const sources: { key: string; label: string; value: number; color: string }[] = [
    { key: "coupons", label: "купоны", value: sum.coupons, color: "var(--good)" },
    { key: "sells", label: "продажи", value: sum.sells, color: "var(--accent)" },
    { key: "redemptions", label: "погашения", value: sum.redemptions, color: "var(--bad)" },
  ];

  return (
    <>
      <Topbar
        title="Статистика"
        subtitle="реализованный P&L после налога — купоны, продажи и погашения"
        right={<span className="stamp">equity-кривая</span>}
      />

      {history.isError && (
        <div className="banner error">ошибка: {String(history.error)}</div>
      )}

      <div ref={scrollRef} className="page-scroll">
        {history.data && events.length === 0 && (
          <div className="banner">
            пока нет реализованных событий — купоны, продажи и погашения появятся
            здесь по мере записи сделок в портфеле
          </div>
        )}

        {events.length > 0 && (
          <>
            <div className="grid g-4" style={{ gap: 12, marginBottom: 14 }}>
              <div className="card" style={{ padding: 14 }}>
                <div className="label">за всё время</div>
                <div className={`serif mono ${sum.total >= 0 ? "ok" : "bad"}`} style={{ fontSize: 26, marginTop: 4 }}>
                  {signed(sum.total)}
                </div>
                <div className="soft" style={{ fontSize: 12, marginTop: 4 }}>
                  {sum.events} событий
                </div>
              </div>
              <div className="card" style={{ padding: 14 }}>
                <div className="label">с начала года</div>
                <div className={`serif mono ${sum.ytd >= 0 ? "ok" : "bad"}`} style={{ fontSize: 26, marginTop: 4 }}>
                  {signed(sum.ytd)}
                </div>
                <div className="soft" style={{ fontSize: 12, marginTop: 4 }}>
                  YTD · {now.getFullYear()}
                </div>
              </div>
              <div className="card" style={{ padding: 14 }}>
                <div className="label">за месяц</div>
                <div className={`serif mono ${sum.month >= 0 ? "ok" : "bad"}`} style={{ fontSize: 26, marginTop: 4 }}>
                  {signed(sum.month)}
                </div>
                <div className="soft" style={{ fontSize: 12, marginTop: 4 }}>
                  текущий месяц
                </div>
              </div>
              <div className="card" style={{ padding: 14 }}>
                <div className="label">структура</div>
                <div
                  style={{
                    display: "flex",
                    height: 8,
                    marginTop: 8,
                    borderRadius: 2,
                    overflow: "hidden",
                    border: "1px solid var(--line)",
                  }}
                >
                  {sources.map((s) => (
                    <div
                      key={s.key}
                      style={{
                        flex: Math.abs(s.value) / structTotal || 0.001,
                        background: s.color,
                        opacity: s.value < 0 ? 0.6 : 1,
                      }}
                    />
                  ))}
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: 11,
                    color: "var(--ink-soft)",
                    marginTop: 6,
                    gap: 4,
                  }}
                >
                  {sources.map((s) => (
                    <span key={s.key}>
                      <span style={{ color: s.color }}>■</span> {s.label}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="card" style={{ padding: 18, marginBottom: 14 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-end",
                  marginBottom: 12,
                  flexWrap: "wrap",
                  gap: 14,
                }}
              >
                <div>
                  <div className="label">реализованный P&L, накопленным итогом</div>
                  <div className={`serif mono ${sum.total >= 0 ? "ok" : "bad"}`} style={{ fontSize: 34, marginTop: 6 }}>
                    {signed(sum.total)}
                  </div>
                  <div className="soft" style={{ fontSize: 12, marginTop: 4 }}>
                    с {fmtDate(events[0].date)} · {sum.events} событий
                  </div>
                </div>
              </div>
              <EquityCurve events={events} />
            </div>

            <div className="grid" style={{ gridTemplateColumns: "1fr 280px", gap: 14 }}>
              <div className="card" style={{ padding: 18 }}>
                <div className="label" style={{ marginBottom: 10 }}>
                  по месяцам
                </div>
                <MonthlyBars months={months} />
                {sum.bestMonth && sum.worstMonth && (
                  <div className="soft" style={{ fontSize: 12, marginTop: 18, fontStyle: "italic" }}>
                    лучший месяц — {sum.bestMonth.label} ({signed(sum.bestMonth.value)});
                    худший — {sum.worstMonth.label} ({signed(sum.worstMonth.value)})
                  </div>
                )}
              </div>

              <div className="card" style={{ padding: 18 }}>
                <div className="label" style={{ marginBottom: 10 }}>
                  по источникам
                </div>
                {sources.map((s) => (
                  <div key={s.key} style={{ marginBottom: 14 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 4 }}>
                      <span>{s.label}</span>
                      <span className={`mono ${s.value >= 0 ? "ok" : "bad"}`}>{signed(s.value)}</span>
                    </div>
                    <div style={{ height: 6, background: "var(--paper-2)", borderRadius: 3, overflow: "hidden" }}>
                      <div
                        style={{
                          width: `${(Math.abs(s.value) / structTotal) * 100}%`,
                          height: "100%",
                          background: s.color,
                          opacity: s.value < 0 ? 0.6 : 1,
                        }}
                      />
                    </div>
                  </div>
                ))}

                <div style={{ borderTop: "1px solid var(--line)", margin: "16px 0 12px" }} />
                <div className="label" style={{ marginBottom: 6 }}>
                  топ-3 эмитента
                </div>
                {issuers.map((s, i) => (
                  <div
                    key={s.name}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 8,
                      padding: "4px 0",
                      borderBottom: i < issuers.length - 1 ? "1px dashed var(--line)" : "none",
                      fontSize: 13,
                    }}
                  >
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {s.name}
                    </span>
                    <span className={`mono ${s.value >= 0 ? "ok" : "bad"}`}>{signed(s.value)}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
